
import { supabase } from "@/integrations/supabase/client";

export class WearableTokenManager {
  private static instance: WearableTokenManager;
  
  private constructor() {}
  
  public static getInstance(): WearableTokenManager {
    if (!WearableTokenManager.instance) {
      WearableTokenManager.instance = new WearableTokenManager();
    }
    return WearableTokenManager.instance;
  }

  /**
   * Check if token needs proactive refresh (6 hours before expiry instead of 30 minutes)
   */
  public static needsRefresh(expiresAt: string): boolean {
    if (!expiresAt) return true;
    
    const expiryTime = new Date(expiresAt).getTime();
    const currentTime = Date.now();
    const sixHoursInMs = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
    
    // Return true if token expires within 6 hours
    return (expiryTime - currentTime) < sixHoursInMs;
  }

  /**
   * Check if a token is expired
   */
  public static isExpired(expiresAt: string): boolean {
    const now = new Date().getTime();
    const expiryTime = new Date(expiresAt).getTime();
    
    return expiryTime <= now;
  }

  /**
   * Proactively refresh token before API calls if needed
   */
  public static async ensureValidToken(connectionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🔍 Checking token validity for connection: ${connectionId}`);
      
      const { data: connection, error } = await supabase
        .from("wearable_connections")
        .select("*")
        .eq("id", connectionId)
        .single();

      if (error || !connection) {
        console.error(`❌ No wearable connection found for connection ${connectionId}:`, error);
        return { success: false, error: 'No wearable connection found' };
      }

      // Check if connection is in circuit breaker state
      if (this.shouldSkipDueToCircuitBreaker(connection)) {
        console.log(`⏭️ Skipping token refresh for ${connection.provider} - circuit breaker active`);
        return { success: false, error: 'Circuit breaker active - too many recent failures' };
      }

      // Check if token is expired or needs proactive refresh
      const isExpired = this.isExpired(connection.expires_at);
      const needsRefresh = this.needsRefresh(connection.expires_at);

      if (!needsRefresh && !isExpired) {
        console.log(`✅ Token is still valid for connection ${connectionId}`);
        return { success: true };
      }

      const refreshReason = isExpired ? 'expired' : 'proactive refresh';
      console.log(`🔄 Token ${refreshReason} for connection ${connectionId}, attempting refresh...`);
      
      // Call the robust refresh token function
      const { data, error: refreshError } = await supabase.functions.invoke("refresh-wearable-token", {
        body: { 
          connectionId: connectionId,
          provider: connection.provider
        },
      });

      if (refreshError || !data?.success) {
        console.error(`❌ Failed to refresh token for connection ${connectionId}:`, refreshError || data?.error);
        return { 
          success: false, 
          error: refreshError?.message || data?.error || 'Token refresh failed' 
        };
      }

      console.log(`✅ Successfully refreshed token for connection ${connectionId}`);
      return { success: true };
    } catch (error) {
      console.error(`💥 Error ensuring valid token for connection ${connectionId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get token health status for a connection
   */
  public static async getTokenHealth(connectionId: string): Promise<{
    isHealthy: boolean;
    status: string;
    expiresAt: string | null;
    lastRefreshAttempt: string | null;
    failureCount: number;
  }> {
    try {
      const { data: connection, error } = await supabase
        .from("wearable_connections")
        .select("expires_at, token_health_status, last_refresh_attempt, refresh_failure_count")
        .eq("id", connectionId)
        .single();

      if (error || !connection) {
        return {
          isHealthy: false,
          status: 'no_connection',
          expiresAt: null,
          lastRefreshAttempt: null,
          failureCount: 0
        };
      }

      const isExpired = this.isExpired(connection.expires_at);
      const needsRefresh = this.needsRefresh(connection.expires_at);
      const hasFailures = (connection.refresh_failure_count || 0) > 0;
      
      console.log(`📊 Token health check for connection ${connectionId}:`, {
        expiresAt: connection.expires_at,
        isExpired,
        needsRefresh,
        tokenHealthStatus: connection.token_health_status,
        refreshFailureCount: connection.refresh_failure_count,
        hasFailures
      });

      // Token is healthy if:
      // 1. Not expired
      // 2. Database status is healthy
      // 3. No excessive failures
      const isHealthy = !isExpired && 
                       connection.token_health_status === 'healthy' && 
                       (connection.refresh_failure_count || 0) < 3;

      // Determine status
      let status = 'healthy';
      if (isExpired) {
        status = 'expired';
      } else if (connection.token_health_status === 'warning' || hasFailures) {
        status = 'warning';
      } else if (needsRefresh) {
        status = 'needs_refresh';
      }

      return {
        isHealthy,
        status,
        expiresAt: connection.expires_at,
        lastRefreshAttempt: connection.last_refresh_attempt || null,
        failureCount: connection.refresh_failure_count || 0
      };
    } catch (error) {
      console.error('❌ Error getting token health:', error);
      return {
        isHealthy: false,
        status: 'error',
        expiresAt: null,
        lastRefreshAttempt: null,
        failureCount: 0
      };
    }
  }

  /**
   * Manually trigger a token refresh for a specific connection
   */
  public static async manualRefresh(connectionId: string, provider: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🔄 Manual token refresh triggered for connection: ${connectionId}`);
      
      const { data, error } = await supabase.functions.invoke("refresh-wearable-token", {
        body: { 
          connectionId: connectionId,
          provider: provider
        },
      });

      if (error || !data?.success) {
        console.error(`❌ Manual token refresh failed for connection ${connectionId}:`, error || data?.error);
        return { 
          success: false, 
          error: error?.message || data?.error || 'Manual token refresh failed' 
        };
      }

      console.log(`✅ Manual token refresh successful for connection ${connectionId}`);
      return { success: true };
    } catch (error) {
      console.error(`💥 Error during manual token refresh for connection ${connectionId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check if connection should be skipped due to circuit breaker
   */
  private static shouldSkipDueToCircuitBreaker(connection: any): boolean {
    const failureCount = connection.refresh_failure_count || 0;
    const lastAttempt = connection.last_refresh_attempt;
    
    // Circuit breaker: skip if too many recent failures
    if (failureCount >= 3 && lastAttempt) {
      const timeSinceLastAttempt = Date.now() - new Date(lastAttempt).getTime();
      // Exponential backoff: wait longer after more failures
      const backoffTime = Math.min(30 * 60 * 1000 * Math.pow(2, failureCount - 3), 4 * 60 * 60 * 1000); // Max 4 hours
      
      if (timeSinceLastAttempt < backoffTime) {
        return true;
      }
    }
    
    return false;
  }
}
